import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/takeUntil';
import { SidebarService } from '../../../services/side-bar.service';
import { ClothingService } from '../../../services/clothing.service';
import { ClothingErrorService } from '../../../services/clothing-error.service';
import { AdminService } from '../../../services/admin.service';
import {
  ClothingCatalogItemList,
  ClothingIssueStats,
  ClothingIssueStatsFilters,
  ClothingOption,
  ClothingUserOption
} from '../../../models/clothing.model';
import { CLOTHING_MENU_OPTIONS } from './clothing.constants';

const CLOTHING_REPORT_ALL_TIME_FROM = '2020-01-01';

interface ClothingReportFilterData {
  catalogItems: ClothingOption[];
  storekeepers: ClothingUserOption[];
  departments: ClothingDepartmentMarkerOption[];
}

interface ClothingDepartmentMarkerOption extends ClothingOption {
  departmentMarkerCode?: string;
}

interface ClothingDepartmentStatsGroup {
  key: string;
  departmentMarkerCode: string;
  departmentLabel: string;
  issuedQuantity: number;
  issueOperationsCount: number;
  totalIssuedUsd: number;
  issuedActiveEmployees: number;
  totalDepartmentEmployees: number;
  rows: ClothingIssueStats[];
}

@Component({
  selector: 'app-clothing-report',
  templateUrl: './clothing-report.component.html',
  styleUrls: ['./clothing-report.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminClothingReportComponent implements OnInit, OnDestroy {
  catalogItems: ClothingOption[] = [];
  departments: ClothingDepartmentMarkerOption[] = [];
  departmentGroups: ClothingDepartmentStatsGroup[] = [];
  expandedDepartmentKey = '';
  rows: ClothingIssueStats[] = [];
  storekeepers: ClothingUserOption[] = [];
  selectedCatalogItemId = '';
  selectedStorekeeperUserId = '';
  fromDate = '';
  toDate = '';
  errorMessage = '';
  isLoading = false;
  isLoadingFilters = false;
  private destroy$ = new Subject<void>();

  constructor(
    private adminService: AdminService,
    private clothingService: ClothingService,
    private clothingErrorService: ClothingErrorService,
    private sidebarService: SidebarService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.sidebarService.setSelectedMenuOption(CLOTHING_MENU_OPTIONS.report);
    this.loadFilterOptions();
    this.loadReport();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get totalIssuedQuantity(): number {
    return this.rows.reduce((sum, row) => sum + row.issuedQuantity, 0);
  }

  get totalOperations(): number {
    return this.rows.reduce((sum, row) => sum + row.issueOperationsCount, 0);
  }

  get totalAmountUsd(): number {
    return this.rows.reduce((sum, row) => sum + row.totalIssuedUsd, 0);
  }

  loadReport() {
    this.clearMessages();
    this.isLoading = true;
    this.clothingService.getIssuedStats(this.createReportFilters())
      .takeUntil(this.destroy$)
      .subscribe(
        (rows) => this.handleReportLoaded(rows),
        (error: HttpErrorResponse) => this.handleReportLoadFailed(error)
      );
  }

  trackByRow(index: number, row: ClothingIssueStats): string {
    return (row.groupId || row.departmentMarkerCode || 'NO_DEPARTMENT') + ':' + row.catalogItemId;
  }

  trackByDepartmentGroup(index: number, group: ClothingDepartmentStatsGroup): string {
    return group.key;
  }

  toggleDepartmentGroup(group: ClothingDepartmentStatsGroup) {
    if (this.isDepartmentExpanded(group)) {
      this.expandedDepartmentKey = '';
      return;
    }
    this.expandedDepartmentKey = group.key;
  }

  isDepartmentExpanded(group: ClothingDepartmentStatsGroup): boolean {
    return !!group && this.expandedDepartmentKey === group.key;
  }

  trackByOption(index: number, option: ClothingOption): string {
    return option.id;
  }

  trackByStorekeeper(index: number, storekeeper: ClothingUserOption): string {
    return storekeeper.id;
  }

  getRecipientsTooltip(row: ClothingIssueStats): string {
    return '';
  }

  private loadFilterOptions() {
    this.isLoadingFilters = true;
    Observable.forkJoin(
      this.clothingService.getCatalogItems(false),
      this.clothingService.getStorekeeperOptions(),
      this.adminService.getGroupsWithDepartmentMarker()
    )
      .takeUntil(this.destroy$)
      .subscribe(
        (response: [ClothingCatalogItemList[], ClothingUserOption[], HttpResponse<ClothingDepartmentMarkerOption[]>]) =>
          this.handleFilterOptionsLoaded(this.createFilterData(response)),
        (error: HttpErrorResponse) => this.handleFilterOptionsFailed(error)
      );
  }

  private createFilterData(
    response: [ClothingCatalogItemList[], ClothingUserOption[], HttpResponse<ClothingDepartmentMarkerOption[]>]
  ): ClothingReportFilterData {
    return {
      catalogItems: this.mapCatalogItems(response[0]),
      storekeepers: this.sortUsers(response[1] || []),
      departments: this.filterDepartments(this.sortDepartments((response[2] && response[2].body) || []))
    };
  }

  private sortDepartments(items: ClothingDepartmentMarkerOption[]): ClothingDepartmentMarkerOption[] {
    return items.slice().sort((left, right) => left.name.localeCompare(right.name));
  }

  private filterDepartments(departments: ClothingDepartmentMarkerOption[]): ClothingDepartmentMarkerOption[] {
    return departments.filter((department) => this.isDepartmentValid(department));
  }

  private isDepartmentValid(department: ClothingDepartmentMarkerOption): boolean {
    return !!(
      department &&
      department.id &&
      department.name &&
      department.name.charAt(0) !== '[' &&
      this.normalizeMarker(department.departmentMarkerCode).length
    );
  }

  private mapCatalogItems(items: ClothingCatalogItemList[]): ClothingOption[] {
    return this.sortOptions(items.map((item) => this.toOption(item)));
  }

  private handleFilterOptionsLoaded(data: ClothingReportFilterData) {
    this.catalogItems = data.catalogItems;
    this.storekeepers = data.storekeepers;
    this.departments = data.departments;
    if (this.rows.length) {
      this.departmentGroups = this.buildDepartmentGroups(this.rows);
    }
    this.isLoadingFilters = false;
    this.markForCheck();
  }

  private handleFilterOptionsFailed(error: HttpErrorResponse) {
    this.isLoadingFilters = false;
    this.showHttpError(error);
  }

  private handleReportLoaded(rows: ClothingIssueStats[]) {
    this.rows = rows || [];
    this.departmentGroups = this.buildDepartmentGroups(this.rows);
    this.expandedDepartmentKey = '';
    this.isLoading = false;
    this.markForCheck();
  }

  private buildDepartmentGroups(rows: ClothingIssueStats[]): ClothingDepartmentStatsGroup[] {
    var groups: {[key: string]: ClothingDepartmentStatsGroup} = {};
    rows.forEach((row) => {
      var key = this.resolveDepartmentKey(row);
      if (!groups[key]) {
        groups[key] = {
          key: key,
          departmentMarkerCode: row.departmentMarkerCode || '',
          departmentLabel: this.resolveDepartmentLabel(row),
          issuedQuantity: 0,
          issueOperationsCount: 0,
          totalIssuedUsd: 0,
          issuedActiveEmployees: 0,
          totalDepartmentEmployees: 0,
          rows: []
        };
      }
      groups[key].issuedQuantity += row.issuedQuantity || 0;
      groups[key].issueOperationsCount += row.issueOperationsCount || 0;
      groups[key].totalIssuedUsd += row.totalIssuedUsd || 0;
      groups[key].issuedActiveEmployees = this.resolveDepartmentValue(
        row,
        'activeEmployeeCount',
        groups[key].issuedActiveEmployees
      );
      groups[key].totalDepartmentEmployees = this.resolveDepartmentValue(
        row,
        'totalEmployeeCount',
        groups[key].totalDepartmentEmployees
      );
      groups[key].rows.push(row);
    });

    this.ensureConfiguredDepartmentGroups(groups);

    return Object.keys(groups).map((key) => groups[key]);
  }

  private ensureConfiguredDepartmentGroups(
    groups: {[key: string]: ClothingDepartmentStatsGroup}
  ): void {
    if (!this.departments.length) {
      return;
    }

    this.departments.forEach((department) => {
      var key = this.resolveDepartmentKey({
        groupId: department.id,
        departmentMarkerCode: department.departmentMarkerCode || ''
      } as ClothingIssueStats);
      if (groups[key]) {
        if (!groups[key].departmentLabel && department.name) {
          groups[key].departmentLabel = department.name;
        }
        return;
      }

      groups[key] = {
        key: key,
        departmentMarkerCode: department.departmentMarkerCode || '',
        departmentLabel: department.name,
        issuedQuantity: 0,
        issueOperationsCount: 0,
        totalIssuedUsd: 0,
        issuedActiveEmployees: 0,
        totalDepartmentEmployees: 0,
        rows: []
      };
    });
  }

  private resolveDepartmentKey(row: ClothingIssueStats): string {
    if (row.groupId) {
      return row.groupId;
    }
    if (row.departmentMarkerCode) {
      return this.resolveDepartmentKeyByMarker(row.departmentMarkerCode);
    }
    return 'NO_DEPARTMENT';
  }

  private resolveDepartmentKeyByMarker(departmentMarkerCode: string): string {
    var departments = this.departments.filter((department) =>
      this.normalizeMarker(department.departmentMarkerCode) === this.normalizeMarker(departmentMarkerCode)
    );
    if (departments.length === 1 && departments[0].id) {
      return departments[0].id;
    }
    return 'MARKER:' + this.normalizeMarker(departmentMarkerCode);
  }

  private resolveDepartmentLabel(row: ClothingIssueStats): string {
    if (row.groupName) {
      return row.groupName;
    }
    if (row.groupId) {
      var department = this.departments.find((item) => item.id === row.groupId);
      if (department && department.name) {
        return department.name;
      }
    }
    if (row.departmentMarkerCode) {
      var markerDepartments = this.departments
        .filter((department) => this.normalizeMarker(department.departmentMarkerCode) === this.normalizeMarker(row.departmentMarkerCode));
      if (markerDepartments.length === 1 && markerDepartments[0].name) {
        return markerDepartments[0].name;
      }
    }
    return row.groupName || row.departmentMarkerCode || '-';
  }

  private normalizeMarker(markerCode?: string): string {
    return (markerCode || '').trim().toLowerCase();
  }

  private resolveDepartmentValue(
    row: ClothingIssueStats,
    field: 'activeEmployeeCount' | 'totalEmployeeCount',
    fallback: number
  ): number {
    return row[field] || fallback;
  }

  private handleReportLoadFailed(error: HttpErrorResponse) {
    this.isLoading = false;
    this.showHttpError(error);
  }

  private formatDate(date: Date): string {
    var month = this.formatDatePart(date.getMonth() + 1);
    var day = this.formatDatePart(date.getDate());

    return date.getFullYear() + '-' + month + '-' + day;
  }

  private formatDatePart(value: number): string {
    if (value < 10) {
      return '0' + value;
    }
    return String(value);
  }

  private createReportFilters(): ClothingIssueStatsFilters {
    return {
      from: this.resolveFromDate(),
      to: this.resolveToDate(),
      catalogItemId: this.selectedCatalogItemId,
      storekeeperUserId: this.selectedStorekeeperUserId
    };
  }

  private resolveFromDate(): string {
    if (this.fromDate.length) {
      return this.fromDate;
    }
    return CLOTHING_REPORT_ALL_TIME_FROM;
  }

  private resolveToDate(): string {
    if (this.toDate.length) {
      return this.toDate;
    }
    return this.formatDate(new Date());
  }

  private toOption(item: ClothingOption): ClothingOption {
    return {
      id: item.id,
      name: item.name
    };
  }

  private sortOptions<T extends ClothingOption>(items: T[]): T[] {
    return items.slice().sort((left, right) => left.name.localeCompare(right.name));
  }

  private sortUsers(items: ClothingUserOption[]): ClothingUserOption[] {
    return items.slice().sort((left, right) => left.fullName.localeCompare(right.fullName));
  }

  private clearMessages() {
    this.errorMessage = '';
  }

  private showHttpError(error: HttpErrorResponse) {
    this.errorMessage = this.resolveErrorMessage(error);
    this.markForCheck();
  }

  private resolveErrorMessage(error: HttpErrorResponse): string {
    return this.clothingErrorService.resolveDefaultMessage(error);
  }

  private markForCheck() {
    this.changeDetectorRef.markForCheck();
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/admin/clothing/clothing-report.component.ts